import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaUser, FaBars, FaTimes, FaShoppingCart } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import Button from "./ui/Button";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "./BrandLogo";

const Navbar = () => {
  const { t } = useTranslation("common");
  const { isAuthenticated, logout } = useAuth();
  const { itemCount } = useCart();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const cartLabel = t("cart") || "購物車";

  const CartIcon = ({ size = 20 }: { size?: number }) => (
    <span className="relative inline-flex">
      <FaShoppingCart size={size} aria-hidden="true" />
      {itemCount > 0 && (
        <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-neon-green text-space text-xs font-bold leading-none">
          {itemCount > 99 ? "99+" : itemCount}
        </span>
      )}
    </span>
  );

  return (
    <nav className="bg-space border-b border-white/5 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="flex items-center">
              <Logo className="h-12 w-auto min-w-[220px] md:min-w-[260px]" />
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-6 ml-auto">
            <Link
              to="/products"
              className="text-body-sm text-gray-300 hover:text-neon-green font-medium transition-colors"
            >
              {t("products") || "產品"}
            </Link>

            <Link
              to="/cart"
              className="text-gray-300 hover:text-neon-green transition-colors p-1"
              aria-label={cartLabel}
            >
              <CartIcon />
            </Link>

            {isAuthenticated ? (
              <div className="relative group">
                <button
                  type="button"
                  className="flex items-center space-x-1 text-body-sm text-gray-300 hover:text-neon-green transition-colors"
                >
                  <FaUser size={20} />
                </button>
                <div className="absolute right-0 w-48 mt-2 py-2 bg-space-light rounded-md shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 border border-white/10">
                  <Link
                    to="/profile"
                    className="block px-4 py-2 text-body-sm text-gray-300 hover:bg-white/5 transition-colors"
                  >
                    {t("profile") || "Profile"}
                  </Link>
                  <Link
                    to="/orders"
                    className="block px-4 py-2 text-body-sm text-gray-300 hover:bg-white/5 transition-colors"
                  >
                    {t("orders") || "Orders"}
                  </Link>
                  <button
                    type="button"
                    onClick={logout}
                    className="block w-full text-left px-4 py-2 text-body-sm text-red-400 hover:bg-white/5 transition-colors"
                  >
                    {t("logout") || "Logout"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="text-body-sm text-gray-300 hover:text-neon-green font-medium transition-colors"
                >
                  {t("login") || "Login"}
                </Link>
                <Link to="/register">
                  <Button
                    size="sm"
                    className="bg-neon-green text-space hover:opacity-90"
                  >
                    {t("register") || "Register"}
                  </Button>
                </Link>
              </div>
            )}
          </div>

          <div className="md:hidden flex items-center gap-1">
            <Link
              to="/cart"
              className="text-gray-300 hover:text-neon-green transition-colors p-2"
              aria-label={cartLabel}
            >
              <CartIcon size={22} />
            </Link>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-300 hover:text-neon-green focus:outline-none rounded-md transition-colors p-2"
              aria-label={
                isMenuOpen
                  ? t("menu.close") || "Close menu"
                  : t("menu.open") || "Open menu"
              }
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? (
                <FaTimes size={24} aria-hidden="true" />
              ) : (
                <FaBars size={24} aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-space-light border-t border-white/5 overflow-hidden"
          >
            <div className="px-4 pt-2 pb-4 space-y-1">
              <Link
                to="/products"
                className="block px-3 py-2 rounded-md text-body font-medium text-gray-300 hover:text-neon-green hover:bg-white/5 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("products") || "Products"}
              </Link>

              <Link
                to="/cart"
                className="block px-3 py-2 rounded-md text-body font-medium text-gray-300 hover:text-neon-green hover:bg-white/5 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {cartLabel}
                {itemCount > 0 ? ` (${itemCount})` : ""}
              </Link>

              {isAuthenticated ? (
                <>
                  <Link
                    to="/profile"
                    className="block px-3 py-2 rounded-md text-body font-medium text-gray-300 hover:text-neon-green hover:bg-white/5 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t("profile") || "Profile"}
                  </Link>
                  <Link
                    to="/orders"
                    className="block px-3 py-2 rounded-md text-body font-medium text-gray-300 hover:text-neon-green hover:bg-white/5 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t("orders") || "Orders"}
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      logout();
                      setIsMenuOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 rounded-md text-body font-medium text-red-400 hover:bg-white/5 transition-colors"
                  >
                    {t("logout") || "Logout"}
                  </button>
                </>
              ) : (
                <div className="pt-4 flex flex-col gap-2">
                  <Link
                    to="/login"
                    onClick={() => setIsMenuOpen(false)}
                    className="block text-center w-full px-4 py-2 rounded-md text-body font-medium text-space bg-neon-green hover:opacity-90"
                  >
                    {t("login") || "Login"}
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setIsMenuOpen(false)}
                    className="block text-center w-full px-4 py-2 border border-white/20 rounded-md text-body font-medium text-gray-300 hover:bg-white/5"
                  >
                    {t("register") || "Register"}
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
